/**
 * EditableProposalField - Interactive field editor with unit toggle
 *
 * Allows users to:
 * - View AI-detected values with confidence scores
 * - Edit values inline
 * - Toggle between monthly/annual units
 * - See warnings for suspicious values
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface EditableField {
  year: string;
  fieldName: string;
  inputValue: number;
  detectedUnit: 'monthly' | 'annual';
  annualizedValue: number;
  confidence: number;
  warnings?: Array<{
    severity: 'high' | 'medium' | 'low';
    message: string;
    suggestion?: string;
  }>;
  isEditable: boolean;
}

interface EditableProposalFieldProps {
  field: EditableField;
  onChange: (field: EditableField) => void;
}

const FIELD_LABELS: Record<string, string> = {
  grossRevenue: 'Revenue',
  costOfGoodsSold: 'COGS',
  rentMortgage: 'Rent/Mortgage',
  utilities: 'Utilities',
  insurance: 'Insurance',
  marketingAdvertising: 'Marketing',
  professionalServices: 'Prof. Services',
  equipmentTechnology: 'Equipment',
  otherOperatingExpenses: 'Other Expenses',
  salaries: 'Salaries',
  payrollTaxes: 'Payroll Taxes',
};

const MONTHLY_FIELDS = new Set([
  'rentMortgage',
  'utilities',
  'insurance',
  'marketingAdvertising',
  'professionalServices',
  'equipmentTechnology',
  'otherOperatingExpenses',
  'salaries',
  'payrollTaxes',
]);

export const EditableProposalField: React.FC<EditableProposalFieldProps> = ({ field, onChange }) => {
  const isMonthlyField = MONTHLY_FIELDS.has(field.fieldName);
  const label = FIELD_LABELS[field.fieldName] || field.fieldName;

  const [isEditing, setIsEditing] = useState(false);
  const [unit, setUnit] = useState<'monthly' | 'annual'>(field.detectedUnit);

  // Display the raw input value - don't recalculate based on unit
  // The unit toggle only changes the INTERPRETATION (multiplier), not the display
  const displayValue = field.inputValue;

  const [editValue, setEditValue] = useState(displayValue.toString());

  // Confidence warning indicator (only show for low/medium confidence)
  const getConfidenceWarning = () => {
    if (field.confidence >= 0.7) {
      return null; // High confidence - no indicator needed
    } else if (field.confidence >= 0.4) {
      return {
        icon: AlertTriangle,
        color: 'text-yellow-600 dark:text-yellow-400',
        message: 'Medium confidence - please verify this value',
      };
    } else {
      return {
        icon: AlertTriangle,
        color: 'text-red-600 dark:text-red-400',
        message: 'Low confidence - this value may need correction',
      };
    }
  };

  const confidenceWarning = getConfidenceWarning();

  const handleSave = () => {
    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) return;

    // Calculate new annualized value based on selected unit
    const newAnnualizedValue = unit === 'monthly' ? numValue * 12 : numValue;

    onChange({
      ...field,
      inputValue: numValue,
      detectedUnit: unit,
      annualizedValue: newAnnualizedValue,
      // Update confidence to high since user manually edited
      confidence: 1.0,
    });

    setIsEditing(false);
  };

  const handleUnitToggle = (newUnit: 'monthly' | 'annual') => {
    if (newUnit === unit) return;

    // Guard against invalid input
    const parsedValue = parseFloat(editValue);
    if (isNaN(parsedValue)) {
      console.warn('[EditableProposalField] Invalid value, cannot toggle unit:', editValue);
      return;
    }

    setUnit(newUnit);

    // Don't change the displayed value - only the interpretation changes
    // The value stays the same, we just change how we interpret it when saving
    const newAnnualizedValue = newUnit === 'monthly' ? parsedValue * 12 : parsedValue;

    onChange({
      ...field,
      inputValue: parsedValue, // Keep the raw input value
      detectedUnit: newUnit,
      annualizedValue: newAnnualizedValue,
      confidence: 1.0,
    });
  };

  const highSeverityWarnings = field.warnings?.filter(w => w.severity === 'high') || [];

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          {/* Left: Label with optional confidence warning */}
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="text-xs text-muted-foreground truncate">{label}:</span>
            {confidenceWarning && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <confidenceWarning.icon
                    className={`h-3 w-3 ${confidenceWarning.color} flex-shrink-0 cursor-help`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[200px]">
                  {confidenceWarning.message}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

        {/* Right: Value editor and unit toggle */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isEditing ? (
            <>
              <Input
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') {
                    setIsEditing(false);
                    setEditValue(displayValue.toString());
                  }
                }}
                className="h-6 w-24 text-xs px-2"
                autoFocus
              />
              <Button
                size="sm"
                onClick={handleSave}
                className="h-6 px-2 text-xs"
              >
                Save
              </Button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="font-medium text-xs hover:text-foreground text-foreground/90 transition-colors"
            >
              ${displayValue.toLocaleString()}
            </button>
          )}

          {/* Unit toggle for monthly fields */}
          {isMonthlyField && (
            <div className="flex items-center border border-border/40 rounded overflow-hidden">
              <button
                onClick={() => handleUnitToggle('monthly')}
                className={`text-[10px] px-1.5 py-0.5 transition-colors ${
                  unit === 'monthly'
                    ? 'bg-foreground text-background'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                /mo
              </button>
              <button
                onClick={() => handleUnitToggle('annual')}
                className={`text-[10px] px-1.5 py-0.5 transition-colors ${
                  unit === 'annual'
                    ? 'bg-foreground text-background'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                /yr
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Warnings */}
      {highSeverityWarnings.length > 0 && (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1">
          <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            {highSeverityWarnings.map((warning, i) => (
              <div key={i}>
                <div>{warning.message}</div>
                {warning.suggestion && (
                  <div className="text-amber-600/70 dark:text-amber-500/70 italic">{warning.suggestion}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
};
